import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GitHubService, GitHubConnection } from '../../core/services/github.service';
import { LinkedTicketsService, LinkedGitHubTicket, TicketComment } from '../../core/services/linked-tickets.service';
import { EnrichmentService, EnrichmentView } from '../../core/services/enrichment.service';
import { ToastService } from '../../core/services/toast.service';

type WorkspaceState = 'loading' | 'not-connected' | 'connected' | 'error';

const PAGE_SIZE = 15;

@Component({
  selector: 'app-github-workspace',
  templateUrl: './github-workspace.component.html',
})
export class GitHubWorkspaceComponent implements OnInit, OnDestroy {
  state: WorkspaceState = 'loading';
  connection: GitHubConnection | null = null;

  tickets: LinkedGitHubTicket[] = [];
  ticketsLoading = true;
  ticketsError   = false;

  // Filtering
  query      = '';
  statusFilter: 'ALL' | 'ACTIVE' | 'RESOLVED' = 'ALL';

  // Pagination
  currentPage = 1;
  readonly pageSize = PAGE_SIZE;

  // Drawer
  selectedTicket: LinkedGitHubTicket | null = null;

  // Comment history
  comments:        TicketComment[] = [];
  commentsLoading  = false;
  commentsError    = false;

  // Comment form
  commentBody    = '';
  commentPending = false;

  // State-change action
  actionPending = false;

  // Enrichment / diagnosis (read-only; never blocks the drawer)
  enrichment:        EnrichmentView | null = null;
  enrichmentLoading = false;

  private connSub?:      Subscription;
  private routeSub?:     Subscription;
  private ticketSub?:    Subscription;
  private commentsSub?:  Subscription;
  private enrichmentSub?: Subscription;

  constructor(
    private readonly github:      GitHubService,
    private readonly tickets$:    LinkedTicketsService,
    private readonly enrichment$: EnrichmentService,
    private readonly toast:       ToastService,
    private readonly route:       ActivatedRoute,
    private readonly router:      Router,
  ) {}

  ngOnInit(): void {
    this.connSub = this.github.getConnection().subscribe({
      next: (conn) => {
        this.connection = conn;
        this.state = conn ? 'connected' : 'not-connected';
        if (conn) this.loadTickets();
      },
      error: () => { this.state = 'error'; },
    });

    this.routeSub = this.route.queryParams.subscribe(params => {
      const ref = params['ghRef'] as string | undefined;
      if (ref && this.tickets.length > 0) {
        this.selectedTicket = this.findTicketByRef(ref);
        if (this.selectedTicket) this.loadEnrichment(this.selectedTicket);
      }
    });
  }

  /**
   * Resolves a deep-link ref to a ticket. UUID refs match a history record's
   * ticketRecordId; legacy `configId:fingerprint` refs match by correlation key.
   */
  private findTicketByRef(ref: string): LinkedGitHubTicket | null {
    const isUuid = /^[0-9a-f-]{36}$/.test(ref);
    if (isUuid) {
      return this.tickets.find(t => t.ticketRecordId === ref) ?? null;
    }
    const [configId, fingerprint] = ref.split(':');
    return this.tickets.find(t => t.configId === configId && t.fingerprint === fingerprint) ?? null;
  }

  ngOnDestroy(): void {
    this.connSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.ticketSub?.unsubscribe();
    this.commentsSub?.unsubscribe();
    this.enrichmentSub?.unsubscribe();
  }

  /**
   * Fetches the finding's diagnosis for the drawer. Deliberately isolated: any
   * failure resolves to `null` (rendered as an honest empty state) and never
   * blocks or errors the rest of the drawer. Mirrors the Jira workspace.
   */
  private loadEnrichment(ticket: LinkedGitHubTicket): void {
    this.enrichment        = null;
    this.enrichmentSub?.unsubscribe();
    if (!ticket.activeFindingId) { this.enrichmentLoading = false; return; }
    this.enrichmentLoading = true;
    this.enrichmentSub = this.enrichment$.getEnrichment(ticket.activeFindingId).subscribe({
      next: ({ enrichment }) => {
        this.enrichment        = enrichment;
        this.enrichmentLoading = false;
      },
      error: () => {
        this.enrichment        = null;
        this.enrichmentLoading = false;
      },
    });
  }

  loadTickets(): void {
    this.ticketsLoading = true;
    this.ticketsError   = false;
    this.ticketSub?.unsubscribe();
    this.ticketSub = this.tickets$.listGitHubTickets().subscribe({
      next: ({ tickets }) => {
        this.tickets        = tickets;
        this.ticketsLoading = false;
        // Restore drawer from query param after first load
        const ref = this.route.snapshot.queryParams['ghRef'] as string | undefined;
        if (ref) {
          this.selectedTicket = this.findTicketByRef(ref);
          if (this.selectedTicket) this.loadEnrichment(this.selectedTicket);
        }
      },
      error: () => {
        this.ticketsLoading = false;
        this.ticketsError   = true;
      },
    });
  }

  // ── Filtering ────────────────────────────────────────────────────────────────

  get filteredTickets(): LinkedGitHubTicket[] {
    let result = this.tickets;

    if (this.statusFilter !== 'ALL') {
      result = result.filter(t => t.status === this.statusFilter);
    }

    const q = this.query.toLowerCase().trim();
    if (q) {
      result = result.filter(t =>
        t.repository.toLowerCase().includes(q) ||
        String(t.issueNumber).includes(q) ||
        (t.resourceName ?? '').toLowerCase().includes(q) ||
        (t.resourceId   ?? '').toLowerCase().includes(q),
      );
    }

    return result;
  }

  // ── Pagination ────────────────────────────────────────────────────────────────

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredTickets.length / this.pageSize));
  }

  get pagedTickets(): LinkedGitHubTicket[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredTickets.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get pageStart(): number {
    return this.filteredTickets.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredTickets.length);
  }

  goToPage(page: number): void {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
  }

  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }

  onQueryChange(): void   { this.currentPage = 1; }
  onStatusChange(): void  { this.currentPage = 1; }

  // ── Comments ─────────────────────────────────────────────────────────────────

  loadComments(): void {
    if (!this.selectedTicket) return;
    this.commentsLoading = true;
    this.commentsError   = false;
    this.commentsSub?.unsubscribe();
    const comments$ = this.selectedTicket.ticketRecordId
      ? this.tickets$.getGitHubCommentsById(this.selectedTicket.ticketRecordId)
      : this.tickets$.getGitHubComments(this.selectedTicket.configId, this.selectedTicket.fingerprint);
    this.commentsSub = comments$.subscribe({
      next: ({ comments }) => {
        this.comments        = comments;
        this.commentsLoading = false;
      },
      error: () => {
        this.commentsLoading = false;
        this.commentsError   = true;
      },
    });
  }

  refreshDrawer(): void {
    this.loadComments();
  }

  // ── Drawer ───────────────────────────────────────────────────────────────────

  openDrawer(ticket: LinkedGitHubTicket): void {
    this.selectedTicket = ticket;
    this.commentBody    = '';
    this.comments       = [];
    this.commentsError  = false;
    this.loadComments();
    this.loadEnrichment(ticket);
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { ghRef: ticket.ticketRecordId ?? `${ticket.configId}:${ticket.fingerprint}` },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeDrawer(): void {
    this.selectedTicket    = null;
    this.commentBody       = '';
    this.comments          = [];
    this.enrichment        = null;
    this.enrichmentLoading = false;
    this.commentsSub?.unsubscribe();
    this.enrichmentSub?.unsubscribe();
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { ghRef: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Actions ───────────────────────────────────────────────────────────────────

  submitComment(): void {
    if (!this.selectedTicket || !this.commentBody.trim() || this.commentPending) return;
    this.commentPending = true;
    const add$ = this.selectedTicket.ticketRecordId
      ? this.tickets$.addGitHubCommentById(this.selectedTicket.ticketRecordId, this.commentBody.trim())
      : this.tickets$.addGitHubComment(this.selectedTicket.configId, this.selectedTicket.fingerprint, this.commentBody.trim());
    add$.subscribe({
      next: (res) => {
        this.commentPending = false;
        if (res.error) {
          this.toast.show(res.error, 'error');
        } else {
          this.commentBody = '';
          this.toast.show('Comment added successfully', 'success');
          this.loadComments();
        }
      },
      error: () => {
        this.commentPending = false;
        this.toast.show('Failed to add comment', 'error');
      },
    });
  }

  setIssueState(state: 'open' | 'closed'): void {
    if (!this.selectedTicket || this.actionPending) return;
    this.actionPending = true;
    const state$ = this.selectedTicket.ticketRecordId
      ? this.tickets$.setGitHubIssueStateById(this.selectedTicket.ticketRecordId, state)
      : this.tickets$.setGitHubIssueState(this.selectedTicket.configId, this.selectedTicket.fingerprint, state);
    state$.subscribe({
      next: (res) => {
        this.actionPending = false;
        if (res.error) {
          this.toast.show(res.error, 'error');
        } else {
          this.toast.show(`Issue ${state === 'closed' ? 'closed' : 're-opened'} successfully`, 'success');
          this.loadTickets();
          this.closeDrawer();
        }
      },
      error: () => {
        this.actionPending = false;
        this.toast.show('Action failed', 'error');
      },
    });
  }

  // ── Utilities ─────────────────────────────────────────────────────────────────

  serviceTypeLabel(t: LinkedGitHubTicket): string {
    const map: Record<string, string> = { LAMBDA: 'Lambda', GLUE: 'Glue', PIPELINE: 'CodePipeline' };
    return map[t.serviceType ?? ''] ?? t.serviceType ?? '—';
  }

  resourceLabel(t: LinkedGitHubTicket): string {
    return t.resourceName ?? t.resourceId ?? '—';
  }

  relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    if (mins < 1)    return 'just now';
    if (mins < 60)   return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs  < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30)   return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  }
}
